import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';

const STORAGE_PREFIX = 'scio3:lastPath:';

/**
 * Persists the current folder path per page in sessionStorage.
 * When user returns to a page with no path in URL, restores the last-visited path.
 *
 * This fixes the issue where switching tabs loses the folder position (#2755).
 *
 * @param pageKey - Unique key for the page (e.g., 'assets', 'screens')
 */
export function usePersistedPath(pageKey: string) {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPath = searchParams.get('path');
  const teamId = searchParams.get('teamId');
  const shared = searchParams.get('shared');
  const prevPathRef = useRef<string | null>(null);

  // On mount: if no path in URL and no deep-link params, restore from sessionStorage
  useEffect(() => {
    // Skip if URL already has a path (direct navigation, deep link, refresh)
    if (currentPath) return;

    // Skip if URL has deep-link params (id, editId, feed_id) — those are intentional
    if (searchParams.get('id') || searchParams.get('editId') || searchParams.get('feed_id')) return;

    const stored = sessionStorage.getItem(STORAGE_PREFIX + pageKey);
    if (stored && stored !== '/') {
      try {
        const params: Record<string, string> = JSON.parse(stored);
        setSearchParams(params, { replace: true });
      } catch {
        // Invalid stored data — ignore
        sessionStorage.removeItem(STORAGE_PREFIX + pageKey);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  // On path change: save to sessionStorage.
  // Persist teamId and shared alongside path so cross-team shared folders
  // restore correctly when returning from another tab (#285).
  //
  // Uses prevPathRef to track transitions: only clear sessionStorage when going
  // FROM a valid path to null/root (explicit root navigation). Mount-time
  // null→null transitions are ignored, so StrictMode double-fire won't
  // wipe the stored path (#311, #281).
  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = currentPath;

    if (currentPath && currentPath !== '/') {
      const params: Record<string, string> = { path: currentPath };
      if (teamId) params.teamId = teamId;
      if (shared) params.shared = shared;
      sessionStorage.setItem(STORAGE_PREFIX + pageKey, JSON.stringify(params));
    } else if (prev && prev !== '/') {
      // Transitioning from a valid path to root/null — clear stored path.
      // This ensures navigating to root is remembered, while mount-time
      // null→null transitions don't wipe the stored path.
      sessionStorage.removeItem(STORAGE_PREFIX + pageKey);
    }
  }, [currentPath, pageKey, teamId, shared]);
}

/** Clears all persisted folder paths from sessionStorage. Called on logout. */
export const clearPersistedPaths = () => {
  Object.keys(sessionStorage)
    .filter((k) => k.startsWith(STORAGE_PREFIX))
    .forEach((k) => sessionStorage.removeItem(k));
};
